import  { useState, useMemo, useEffect } from "react";
import { AutoComplete, Spin } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import _ from "lodash";

type SearchOption = Record<string, unknown> & { id?: string | number };

type SearchCompProps<T extends SearchOption> = {
  onSelect: (option: T | null) => void;
  options: T[] | null;
  toDisplay: string[];
  isDisabled?: boolean;
  value: string | undefined;
  onChangeValue: (val: string) => void;
};

type SearchResult<T extends SearchOption> = {
  title: string;
  option: T | null;
  key: string | number;
  disabled?: boolean;
  className?: string;
};

function SearchInput<T extends SearchOption>({
  onSelect,
  options,
  toDisplay,
  isDisabled,
  value,
  onChangeValue,
}: SearchCompProps<T>) {
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<SearchResult<T>[]>([]);

  const debouncedFilter = useMemo(
    () =>
      _.debounce((searchText: string) => {
        const re = new RegExp(_.escapeRegExp(searchText), "i");

        const filteredResults: SearchResult<T>[] = options
          ? options
              .filter((option) =>
                re.test(
                  `${toDisplay.map((item: string) => String(option[item] ?? "")).join(" ")}`,
                ),
              )
              .map((option, idx: number) => ({
                title: toDisplay.map((item) => String(option[item] ?? "")).join(" "),
                option,
                key: option.id ?? idx,
              }))
          : [
              {
                title: "No results found",
                option: null,
                disabled: true,
                className: "no-results",
                key: "no-results",
              },
            ];

        if (filteredResults.length === 0) {
          setResults([
            {
              title: "No results found",
              option: null,
              disabled: true,
              className: "no-results",
              key: "no-results",
            },
          ]);
        } else {
          setResults(filteredResults);
        }
        setIsLoading(false);
      }, 300),
    [options, toDisplay],
  );

  useEffect(() => {
    return () => {
      debouncedFilter.cancel();
    };
  }, [debouncedFilter]);

  useEffect(() => {
    if (!options || !value) {
      setResults([]);
      setIsLoading(false);
      return;
    }

    const normalizedValue = value.trim().toLowerCase();

    const filteredResults: SearchResult<T>[] = options
      .map((option) => {
        const title = toDisplay
          .map((item: string) => String(option[item] ?? ""))
          .join(" ");
        return {
          title,
          option,
          key: option.id ?? title,
        };
      })
      .filter((result) =>
        result.title.toLowerCase().includes(normalizedValue),
      );

    if (filteredResults.length === 0 && normalizedValue !== "") {
      filteredResults.push({
        title: value,
        option: null,
        key: "manual-entry",
        disabled: true,
        className: "no-results",
      });
    }

    setResults(filteredResults);
    setIsLoading(false);
  }, [value, options, toDisplay]);

  const handleSearchChange = (searchText: string) => {
    const safeSearchText = typeof searchText === "string" ? searchText : "";

    if (!safeSearchText.trim()) {
      debouncedFilter.cancel();
      setIsLoading(false);
      setResults([]);
      return;
    }

    setIsLoading(true);
    debouncedFilter(safeSearchText);
  };

  const handleInputChange = (nextValue: string) => {
    const safeNextValue = typeof nextValue === "string" ? nextValue : "";

    onChangeValue(safeNextValue);
    if (!safeNextValue.trim()) {
      debouncedFilter.cancel();
      setIsLoading(false);
      setResults([]);
    }
  };

  const handleResultSelect = (
    selectedValue: string,
    option: { disabled?: boolean; option: T | null },
  ) => {
    if (option?.disabled) return;
    debouncedFilter.cancel();
    setIsLoading(false);
    onChangeValue(selectedValue);
    onSelect(option?.option ?? null);
  };

  const autoCompleteOptions = results.map((result) => ({
    value: result.title,
    label: result.title,
    option: result.option,
    disabled: result.disabled,
    key: result.key,
  }));

  return (
    <AutoComplete
      options={autoCompleteOptions}
      value={value}
      showSearch={{onSearch: handleSearchChange
      }}

      onSelect={handleResultSelect}
      onChange={handleInputChange}
      disabled={isDisabled}
      allowClear
      className="w-full"
      prefix={<SearchOutlined className="text-slate-400" />}
      notFoundContent={isLoading ? <Spin size="small" /> : null}
      placeholder="Search..."
    />

  );
}

export default SearchInput;
